// app.routes.ts
import { Route } from '@angular/router';
import { LayoutComponent } from 'app/layout/layout.component';
import { initialDataResolver } from 'app/app.resolvers';

import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { aquacultureChildren } from './app-aquaculture.routes';
import { wildHarvestChildren } from './app-wild-harvest.routes';

import { CUSTOMER_CONFIG } from './core/customer/customer.config';
import { canMatchCustomerApp } from './core/customer/customer-feature.guard';

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const storedApp = localStorage.getItem('currentApp');

      const app = storedApp === 'aquaculture' || storedApp === 'wild-harvest' ? storedApp : CUSTOMER_CONFIG.defaultApp;

      return CUSTOMER_CONFIG.defaultRedirect[app];
    },
  },

  {
    path: '',
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: { layout: 'empty' },
    children: [
      {
        path: 'confirmation-required',
        loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes'),
      },
      {
        path: 'forgot-password',
        loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes'),
      },
      {
        path: 'reset-password',
        loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes'),
      },
      {
        path: 'sign-in',
        loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes'),
      },
      {
        path: 'sign-up',
        loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes'),
      },
    ],
  },

  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: { layout: 'empty' },
    children: [
      {
        path: 'sign-out',
        loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes'),
      },
      {
        path: 'unlock-session',
        loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes'),
      },
      {
        path: 'main',
        pathMatch: 'full',
        redirectTo: () => {
          const enabledApps = Object.keys(CUSTOMER_CONFIG.apps).filter((key) => CUSTOMER_CONFIG.apps[key as keyof typeof CUSTOMER_CONFIG.apps].enabled);

          if (enabledApps.length === 1) {
            const app = enabledApps[0] as keyof typeof CUSTOMER_CONFIG.defaultRedirect;
            return CUSTOMER_CONFIG.defaultRedirect[app];
          }

          return 'main-select';
        },
      },
      {
        path: 'main-select',
        loadComponent: () => import('./modules/app-select/app-select.component').then((m) => m.AppSelectComponent),
      },
    ],
  },

  {
    path: 'cte/processing',
    redirectTo: 'aquaculture/ctes/processing',
    pathMatch: 'full',
  },

  {
    path: 'aquaculture',
    canMatch: [canMatchCustomerApp],
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: { layout: 'dense', appKey: 'aquaculture' },
    resolve: { initialData: initialDataResolver },
    runGuardsAndResolvers: 'always',
    children: aquacultureChildren,
  },

  {
    path: 'wild-harvest',
    canMatch: [canMatchCustomerApp],
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: { layout: 'dense', appKey: 'wild-harvest' },
    resolve: { initialData: initialDataResolver },
    runGuardsAndResolvers: 'always',
    children: wildHarvestChildren,
  },

  {
    path: 'unauthorized',
    component: LayoutComponent,
    data: { layout: 'empty' },
    children: [
      {
        path: '',
        loadComponent: () => import('./modules/auth/unauthorized/unauthorized.component').then((m) => m.UnauthorizedComponent),
      },
    ],
  },

  { path: '**', redirectTo: 'sign-in' },
];
